<template>
  <div data-component="agent-status-item-settings">
    <or-input
      data-input="status-key"
      :label="$t('hitl.settings.agent-status.key-label')"
      :placeholder="$t('hitl.settings.agent-status.key-placeholder')"
      :model-value="item.key"
      :required="true"
      :maxlength="256"
      :disabled="item.isBasic"
      :error="errors.key"
      @input="onKeyChange"
    />

    <or-select
      :label="$t('hitl.settings.agent-status.status-label')"
      :model-value="item.status"
      :options="statusOptions"
      :disabled="true"
      @update:modelValue="onStatusChange"
    />

    <or-input
      data-input="status-label"
      :label="$t('hitl.settings.agent-status.description-label')"
      :placeholder="placeholders.label"
      :model-value="item.label"
      :required="true"
      :maxlength="256"
      :disabled="item.isBasic"
      :error="errors.label"
      @update:modelValue="onLabelChange"
    />

    <or-input
      data-input="status-icon"
      :label="$t('hitl.settings.agent-status.icon-label')"
      :placeholder="placeholders.icon"
      :model-value="item.icon"
      :maxlength="256"
      @input="onIconChange"
    />

    <or-input
      data-input="status-expire-in"
      :label="$t('hitl.settings.agent-status.expire-in-label')"
      :placeholder="$t('hitl.settings.agent-status.expire-in-placeholder')"
      :model-value="expireInMinutes"
      @input="onExpireInChange"
    />

    <or-checkbox
      data-checkbox="status-toggle-on"
      :model-value="item.canToggle.includes('on')"
      :label="$t('hitl.settings.agent-status.can-toggle-on-label')"
      @update:modelValue="onCanToggleChange('on')"
    />

    <or-checkbox
      data-checkbox="status-toggle-off"
      :label="$t('hitl.settings.agent-status.can-toggle-off-label')"
      :model-value="item.canToggle.includes('off')"
      @update:modelValue="onCanToggleChange('off')"
    />
  </div>
</template>

<script lang="ts">
import _ from 'lodash';
import { defineComponent, PropType } from 'vue-demi';
import {
  OrInputV3 as OrInput,
  OrSelectV3 as OrSelect,
  OrCheckboxV3 as OrCheckbox,
} from '@onereach/ui-components-25';

import { AgentStatus } from '@/types';
import {
  AGENT_STATUS_API,
  AGENT_STATUS_LABEL,
  DEFAULT_STATUS_ICONS,
} from '@/constants';

export default defineComponent({
  name: 'AgentStatusItemSettings',
  components: { OrInput, OrSelect, OrCheckbox },
  props: {
    item: {
      type: Object as PropType<AgentStatus>,
      default: () => ({}),
    },
    errors: {
      type: Object as PropType<{ key: string; label: string; }>,
      default: () => ({}),
    },
  },
  emits: ['item-change'],
  data() {
    return {
      KEY_REGEX: /^[_\-\w]*$/g,
      NUMBER_REGEX: /^[0-9]*$/g,
      DEFAULT_EXPIRE_IN: 3 * 60,
      MAX_EXPIRE_IN: 24 * 60 * 60,
    };
  },
  computed: {
    placeholders() {
      return {
        label: this.$t(AGENT_STATUS_LABEL[this.item.status]),
        icon: DEFAULT_STATUS_ICONS[this.item.status],
      };
    },

    /** User enters the value in minutes */
    expireInMinutes() {
      const { expireIn = '' } = this.item;
      return expireIn ? expireIn / 60 : '';
    },

    statusOptions() {
      const statuses = Object.values(AGENT_STATUS_API);
      const options = [];

      for (const status of statuses) {
        const label = this.$t(AGENT_STATUS_LABEL[status]);
        options.push({ value: status, label });
      }

      return options;
    },
  },
  methods: {
    /** Keys usually look like UUIDs */
    onKeyChange(event: KeyboardEvent) {
      const target = event.target as HTMLInputElement;
      const input = target.value;

      if (input && !new RegExp(this.KEY_REGEX).test(input)) {
        target.value = this.item.key;
        return;
      }

      this.onItemChange({ key: input });
    },

    /** Auto-input a key based on description label */
    onLabelChange(input: string) {
      const matchesKey = this.item.key === _.kebabCase(this.item.label);
      const key = !this.item.key || matchesKey
        ? _.kebabCase(input)
        : this.item.key;

      this.onItemChange({ label: input, key });
    },

    onStatusChange(status: AGENT_STATUS_API) {
      this.onItemChange({ status });
    },

    /** Icons can look like "my-icon" */
    onIconChange(event: KeyboardEvent) {
      const target = event.target as HTMLInputElement;
      const input = target.value;

      if (input && !new RegExp(this.KEY_REGEX).test(input)) {
        target.value = this.item.icon || '';
        return;
      }

      this.onItemChange({ icon: input });
    },

    /** Value is a number in 1-24*60 minutes range */
    onExpireInChange(event: KeyboardEvent) {
      const target = event.target as HTMLInputElement;
      const input = target.value;

      /* Allow only integer values */
      if (input && !new RegExp(this.NUMBER_REGEX).test(input)) {
        target.value = `${this.expireInMinutes}`;
        return;
      }

      let expireIn = Number(input) * 60;
      if (expireIn > this.MAX_EXPIRE_IN) {
        expireIn = this.MAX_EXPIRE_IN;
        target.value = `${this.MAX_EXPIRE_IN / 60}`;
      }

      if (expireIn === 0) {
        target.value = '';
        expireIn = this.DEFAULT_EXPIRE_IN;
      }

      this.onItemChange({ expireIn });
    },

    /** Order values like ["on", "off"] */
    onCanToggleChange(property: 'on' | 'off') {
      const canToggle = [...this.item.canToggle];
      const index = canToggle.indexOf(property);

      index !== -1
        ? canToggle.splice(index, 1)
        : property === 'on'
          ? canToggle.unshift('on')
          : canToggle.push('off');

      this.onItemChange({ canToggle });
    },

    onItemChange(changes: object) {
      const item = { ..._.cloneDeep(this.item), ...changes };
      this.$emit('item-change', item);
    },
  },
});
</script>

<style lang="scss" scoped>
[data-component='agent-status-item-settings'] {
  display: flex;
  flex-direction: column;
  gap: var(--s-4);
  width: 100%;
}
</style>
